import {Injectable} from '@angular/core';
import {LoginRequestModel} from '../models/login-request-model';
import {firstValueFrom} from 'rxjs';
import {RefreshTokenResponseModel} from '../models/refresh-token-response-model';
import {LoginResponseModelNew} from '../models/login-response-model-new';
import {HttpHeaders} from '@angular/common/http';
import {HttpServiceBase} from '../../../core/services/http-service-base';
import {AppConstants} from '../../../core/app-constants';


@Injectable({providedIn: 'root'})
export class AuthHttpService extends HttpServiceBase {

    private readonly SKIP_AUTH_HEADER = {headers: new HttpHeaders({'X-Skip-Auth': 'true'})};
    private readonly USE_SECURITY_VER = {headers: new HttpHeaders({'X-Use-Security-Auth-Version': 'true'})};



    // https://minntgmbh.atlassian.net/wiki/spaces/BROT/pages/3770122269/LOGIN+external+authentication+v1
    async loginNew(credentials: LoginRequestModel): Promise<LoginResponseModelNew> {
        const headers = this.SKIP_AUTH_HEADER.headers.set('Application', 'PARTNER_APP');

        return await firstValueFrom(
            this.httpClient.post<LoginResponseModelNew>(
                `${this.securityServerAddress}${AppConstants.API_LOGIN_URL_NEW}`,
                credentials,
                { headers }
            )
        );
    }


    // https://minntgmbh.atlassian.net/wiki/spaces/BROT/pages/3795550211/VERIFY+2FA+v1
    async verify2FA(verificationHash: string, verificationCode: string): Promise<LoginResponseModelNew> {
        const url = `${this.securityServerAddress}/v1/security/2fa/verify`;
        const payload = {verificationHash, verificationCode};

        const headers = this.SKIP_AUTH_HEADER.headers.set('Application', 'PARTNER_APP');

        return await firstValueFrom(
            this.httpClient.post<LoginResponseModelNew>(url, payload, { headers })
        );

    }

    // https://minntgmbh.atlassian.net/wiki/spaces/BROT/pages/3769696270/REFRESH+Token+v1
    async refreshTokenNew(refreshToken: string): Promise<LoginResponseModelNew> {
        const url = `${this.securityServerAddress}/v1/security/refresh`;
        const payload = {refreshToken};

        return await firstValueFrom(
            this.httpClient.post<LoginResponseModelNew>(url, payload, this.SKIP_AUTH_HEADER)
        );
    }


    // https://minntgmbh.atlassian.net/wiki/spaces/BROT/pages/3797614593/RESET+Password+v1
    async resetPassword(email: string): Promise<void> {
        const url = `${this.securityServerAddress}/v1/security/resetPassword`;
        const payload = {email};

        return await firstValueFrom(
            this.httpClient.post<void>(url, payload, this.SKIP_AUTH_HEADER)
        );
    }


    // https://minntgmbh.atlassian.net/wiki/spaces/BROT/pages/3770122286/AUTHORIZE+REQUEST+v1
    async authorizeRequest(accessToken: string): Promise<LoginResponseModelNew> {
        const url = `${this.securityServerAddress}/v1/security/authorize`;
        const payload = {accessToken};

        return await firstValueFrom(
            this.httpClient.post<LoginResponseModelNew>(url, payload, this.SKIP_AUTH_HEADER)
        );
    }



    // https://minntgmbh.atlassian.net/wiki/spaces/BROT/pages/3798859777/VALIDATE+Password+Verification+Hash+v1
    async validatePasswordVerificationHash(verificationHash: string, verificationHashType: string): Promise<void> {
        console.error('validatePasswordVerificationHash is used only by backend, it should not be called from frontend');
    }


    // https://minntgmbh.atlassian.net/wiki/spaces/BROT/pages/3798925323/FINISH+Set+Reset+Password+v1
    async finishSetResetPassword(verificationHash: string, verificationHashType: string ,password: string): Promise<void> {
        const url = `${this.securityServerAddress}/v1/security/password/${verificationHash}`;
        const payload = {password, verificationHashType};

        return await firstValueFrom(
            this.httpClient.post<void>(url, payload, this.SKIP_AUTH_HEADER)
        );
    }


    // https://minntgmbh.atlassian.net/wiki/spaces/BROT/pages/3806035969/RESEND+2FA+verification+code+v1
    resend2FACode(verificationHash: string): Promise<{ verificationHash: string }> {
        const url = `${this.securityServerAddress}/v1/security/2fa/resend`;
        const payload = {verificationHash};

        return firstValueFrom(  this.httpClient.post<{ verificationHash: string }>(url, payload, this.SKIP_AUTH_HEADER)  );
    }


    // https://minntgmbh.atlassian.net/wiki/spaces/BROT/pages/3803906049/VERIFY+Device+Session+status+v1
    async verifyDeviceSessionStatus(): Promise<LoginResponseModelNew> {
        const url = `${this.securityServerAddress}/v1/security/deviceSession/status`;
        return await firstValueFrom(  this.httpClient.get<any>(url, this.USE_SECURITY_VER) );
    }


    // https://minntgmbh.atlassian.net/wiki/spaces/BROT/pages/3803906063/LOGOUT+v1
    async logout(logoutType: string): Promise<void> {
        const url = `${this.securityServerAddress}/v1/security/logout`;
        await firstValueFrom(this.httpClient.post<void>(url, {logoutType}, this.USE_SECURITY_VER));
    }

    // https://minntgmbh.atlassian.net/wiki/spaces/BROT/pages/3803906078/UPDATE+Password+v1

    async updatePassword(userId: string, oldPassword: string, newPassword: string): Promise<void> {
        const url = `${this.securityServerAddress}/v1/security/user/${userId}/password`;
        const payload = {
            oldPassword,
            password: newPassword
        };

        await firstValueFrom( this.httpClient.put<void>(url, payload, this.USE_SECURITY_VER)  );
    }
}
